import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useOpsOverview } from '../hooks/useDailyInstallations';
import {
  BRAND, Note, PageHeader, States, monthName, num, selectClass, thisMonth,
} from '../components/OpsPageKit';

// Operations Reporting — the front door for the nine rebuilt pages.
//
// One card per page, carrying the single number that page exists to produce.
// Every figure comes from that page's own service, so a headline here and the
// figure on the page it links to cannot drift apart.
//
// Cards carry their own caveat where they have one, because a summary is
// exactly where caveats get lost: the month that is only half covered, the
// engineers with no target, the reviews that are last month's because they are
// only submitted at month end.
const TONES = {
  good: { bar: '#16A34A', text: '#166534' },
  warn: { bar: '#D97706', text: '#92400E' },
  bad: { bar: '#DC2626', text: '#991B1B' },
  neutral: { bar: BRAND, text: '#581678' },
};

function format(card) {
  if (card.format === 'percent') return `${(Number(card.value || 0) * 100).toFixed(1)}%`;
  if (card.format === 'text') return card.value || '—';
  return num(card.value);
}

function Card({ card }) {
  const tone = TONES[card.tone] || TONES.neutral;
  const long = card.format === 'text' && String(card.value || '').length > 14;

  return (
    <Link
      to={card.url}
      className="group flex flex-col rounded-lg border border-gray-200 bg-white p-4 transition hover:border-brand hover:shadow-sm"
    >
      <div className="flex items-start justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-800">{card.title}</h2>
        <ArrowRightIcon className="h-4 w-4 shrink-0 text-gray-300 transition group-hover:text-brand" />
      </div>

      <p
        className={`mt-3 font-bold tabular-nums ${long ? 'text-xl' : 'text-3xl'}`}
        style={{ color: tone.text }}
      >
        {format(card)}
      </p>
      <p className="mt-1 text-xs text-gray-500">{card.caption}</p>

      {card.secondary ? (
        <p className="mt-2 text-xs font-medium" style={{ color: tone.bar }}>{card.secondary}</p>
      ) : null}

      {/* The qualifier travels with the number. */}
      {card.note ? (
        <p className="mt-2 border-t border-gray-100 pt-2 text-[11px] leading-snug text-amber-700">
          {card.note}
        </p>
      ) : null}

      <span className="mt-auto" />
    </Link>
  );
}

function OperationsReporting() {
  const [month, setMonth] = useState(thisMonth());
  const [region, setRegion] = useState('');

  const filters = useMemo(() => ({ month, region }), [month, region]);
  const {
    data, isLoading, isFetching, refetch, isError,
  } = useOpsOverview(filters);

  const c = data?.caveats || {};
  const extPct = c.completed_total
    ? Math.round((c.externals_recorded / c.completed_total) * 100) : 0;

  return (
    <div className="p-6">
      <PageHeader
        title="Operations Reporting"
        subtitle="The nine operations reports, and where each one stands this month."
        month={month}
        onMonth={setMonth}
        onRefresh={refetch}
        fetching={isFetching}
      >
        <select value={region} onChange={(e) => setRegion(e.target.value)} className={selectClass}>
          <option value="">All regions</option>
          <option value="Region N">Region N</option>
          <option value="Region B">Region B</option>
        </select>
      </PageHeader>

      <States isLoading={isLoading} isError={isError} what="the reporting overview" />

      {data && (
        <>
          {c.partial_month ? (
            <Note tone="red">
              <strong>{monthName(month)} is not fully covered.</strong>{' '}
              The installations mirror starts on{' '}
              {new Date(c.data_from).toLocaleDateString('en-GB')}, so every figure below counts only
              part of the month.
            </Note>
          ) : null}

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.cards.map((card) => <Card key={card.key} card={card} />)}
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <p className="text-xs text-gray-600">
              Each figure is produced by the page it links to, not recalculated here, so the two
              always agree. Where a report cannot fully answer its own question the card says so.
              The one caveat that runs through all of them: an externals status is recorded on{' '}
              <strong>{num(c.externals_recorded)} of {num(c.completed_total)} completed jobs</strong>
              {' '}({extPct}%), so any weighting that depends on it applies to that share of the work
              and no more.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default OperationsReporting;
